import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseComponent } from '@isophi/core-ui';
import { ChildGroupList, ChildGroupProfile, ChildrenService, ClassesService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { GroupAndChildrenFilterForm } from '../../constants/forms';
import { GroupAndChildrenFilterService } from '../../services/group-and-children-filter/group-and-children-filter.service';

interface ChildGroupListWithLabel extends ChildGroupList {
  label: string;
}

@Component({
  selector: 'app-group-and-children-filter',
  templateUrl: './group-and-children-filter.component.html',
  styleUrls: ['./group-and-children-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GroupAndChildrenFilterComponent extends BaseComponent implements OnInit {
  @Input() showGroup: boolean;
  @Input() showChildren: boolean;

  protected readonly groupAndChildrenFilterService = inject(GroupAndChildrenFilterService);
  protected readonly classesService = inject(ClassesService);
  private readonly translocoService = inject(TranslocoService);
  protected readonly childrenService = inject(ChildrenService);

  form = new FormGroup<GroupAndChildrenFilterForm>({
    groupUuid: new FormControl<string | null>(null),
    childrenUuids: new FormControl<string[]>([]),
  });

  placeholderText = this.translocoService.translate('childrenPortfolio/children/selectPlaceholder');

  fetchGroups$ = new Subject<string | undefined>();
  groups$: Observable<ChildGroupListWithLabel[]>;

  private readonly classChildren$ = new BehaviorSubject<ChildGroupProfile[]>([]);
  fetchChildren$ = new BehaviorSubject<string | null>(null);

  children$ = combineLatest([this.fetchChildren$, this.classChildren$]).pipe(
    debounceTime(300),
    map(([search, children]) => this.annotateChildrenLabel(this.searchChildren(children, search))),
    takeUntil(this.ngUnsubscribe$)
  );

  ngOnInit(): void {
    this.groupAndChildrenFilterService.selectedGroupUuid$.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (uuid) => {
        this.form.patchValue({ groupUuid: uuid }, { emitEvent: false });
        if (uuid) {
          this.loadChildren(uuid);
        } else {
          this.classChildren$.next([]);
        }
      },
    });

    this.groupAndChildrenFilterService.selectedChildrenUuids$.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (uuids) => {
        this.form.patchValue({ childrenUuids: uuids }, { emitEvent: false });
        this.updatePlaceholder(uuids?.length ?? 0);
      },
    });

    this.form
      .get('groupUuid')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((uuid) => {
        this.groupAndChildrenFilterService.setSelectedGroupUuid(uuid);
        // reset selectedchildren ids
        this.form.get('childrenUuids')?.setValue([]);
        this.groupAndChildrenFilterService.setSelectedChildrenUuids([]);
      });

    this.form
      .get('childrenUuids')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe$), startWith(this.form.get('childrenUuids')?.value ?? []))
      .subscribe((uuids: string[]) => {
        this.groupAndChildrenFilterService.setSelectedChildrenUuids(uuids);
        this.updatePlaceholder(uuids?.length ?? 0);
      });

    this.loadGroups();
  }

  private loadGroups(): void {
    this.groups$ = this.fetchGroups$.pipe(
      startWith<string | undefined>(undefined),
      debounceTime(300),
      switchMap((search: string | undefined) => this.classesService.classesList(false, 1000, undefined, search)),
      map((resp) => resp.results as ChildGroupList[]),
      tap((groups) => {
        if (groups.length === 0) {
          this.form.patchValue({ groupUuid: null, childrenUuids: [] }, { emitEvent: false });
          this.groupAndChildrenFilterService.setSelectedGroupUuid(null);
          this.groupAndChildrenFilterService.setSelectedChildrenUuids([]);
        }
      }),
      // set innitial group if no or invalid group is selected
      tap((groups) => {
        if (!groups || groups?.length === 0) {
          return;
        }

        const usedGroupValue = this.form.controls.groupUuid?.value;
        const isCurrentUsedGroupValid = groups.some((val) => val.uuid === usedGroupValue);

        if (groups.length > 0 && !isCurrentUsedGroupValid) {
          this.form.patchValue({ groupUuid: groups[0].uuid });
        }
      }),
      map(
        (groups) =>
          groups.map((group) => ({
            ...group,
            label: `${group.name} (${group.childCount})`,
          })) as ChildGroupListWithLabel[]
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
      takeUntil(this.ngUnsubscribe$)
    ) as Observable<ChildGroupListWithLabel[]>;
  }

  private loadChildren(groupUuid: string) {
    this.classesService
      .classesRetrieve(groupUuid)
      .pipe(map((classDetail) => classDetail.children))
      .subscribe({
        next: (children) => this.classChildren$.next(children),
      });
  }

  private searchChildren(children: ChildGroupProfile[], search: string) {
    if (!search) {
      return children;
    }

    return children.filter(
      (child) => child.firstName.toLowerCase().includes(search.toLowerCase()) || child.lastName.toLowerCase().includes(search.toLowerCase())
    );
  }

  private annotateChildrenLabel = (children: ChildGroupProfile[]) => {
    return children.map((child) => {
      return {
        ...child,
        label: `${child.firstName} ${child.lastName}`,
      };
    });
  };

  private updatePlaceholder(count: number) {
    this.placeholderText =
      count > 0
        ? `${this.translocoService.translate('childrenPortfolio/children/selectPlaceholder')} (${count})`
        : this.translocoService.translate('childrenPortfolio/children/selectPlaceholder');
  }
}
